import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from '@vis.gl/react-google-maps';
import { useIntl, FormattedMessage } from 'react-intl';
import { LOCALE_CONFIG } from '../../../context/translations/locales';
import ChargingStationsMapMarker from '../ChargingStationsMapMarker/ChargingStationsMapMarker';
import {
  CustomFullscreenControl,
  CustomCameraControl,
} from './MapNavigationControls';

// Safety net for AdvancedMarker's "getRootNode" race condition (see
// MarkersReadyGate below) and for the Maps JavaScript API failing outright
// (auth error, offline): either can throw synchronously during render, and
// this app has no top-level error boundary, so an uncaught error here would
// otherwise blank out the entire page instead of just the map widget.
// MarkersReadyGate should prevent the race condition from firing at all, but
// this bounded retry covers cases where it still slips through before
// falling back to a permanent message.
const MAP_ERROR_MAX_RETRIES = 2;
const MAP_ERROR_RETRY_DELAY_MS = 500;

class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
    this.retryTimeoutId = null;
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Google Map failed to render', error, info);

    // Every marker on the map fails at once and each independently re-enters
    // componentDidCatch for the same failed commit, so guard against
    // scheduling a retry per marker instead of once per actual failure.
    if (this.retryTimeoutId) return;

    if (this.state.retryCount < MAP_ERROR_MAX_RETRIES) {
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null;
        this.setState((state) => ({
          hasError: false,
          retryCount: state.retryCount + 1,
        }));
      }, MAP_ERROR_RETRY_DELAY_MS);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.retryTimeoutId);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="MapLoadError">
          <FormattedMessage
            id="map.loadError"
            defaultMessage="This page didn't load Google Maps correctly. Please refresh the page or try again later."
            description="Shown when the Google Map fails to render"
          />
        </div>
      );
    }
    return this.props.children;
  }
}

const INITIAL_ZOOM = 15;
const API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
// AdvancedMarker requires a Map ID. DEMO_MAP_ID is Google's public id for
// prototyping only (it renders with a "for development purposes" watermark);
// replace with a real Map ID from Google Cloud Console for production.
const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAP_LANGUAGE_BY_LOCALE = Object.values(LOCALE_CONFIG).reduce(
  (languages, { code, intlLocale, googleTarget }) => ({
    ...languages,
    [code]: googleTarget,
    [intlLocale]: googleTarget,
  }),
  {}
);
let loadedGoogleMapLanguage = null;

const resolveGoogleMapLanguage = (locale) => {
  if (loadedGoogleMapLanguage) return loadedGoogleMapLanguage;

  loadedGoogleMapLanguage =
    GOOGLE_MAP_LANGUAGE_BY_LOCALE[locale] || LOCALE_CONFIG.EN.googleTarget;
  return loadedGoogleMapLanguage;
};

// APIProvider's library-loading effect depends on this array by reference,
// not by content, so a fresh array literal on every render (e.g. passed
// inline as a prop) re-triggers it every render. Since loading is async and
// state updates lag behind renders, that can fire concurrent, redundant
// calls to google.maps.importLibrary before the first one resolves --
// which is what was causing the map's default UI controls (fullscreen,
// street view) to render duplicated. A single stable reference fixes it.
const MAP_LIBRARIES = ['places', 'routes'];

const boundsLiteralToCorners = (bounds) => ({
  nw: { lat: bounds.north, lng: bounds.west },
  ne: { lat: bounds.north, lng: bounds.east },
  sw: { lat: bounds.south, lng: bounds.west },
  se: { lat: bounds.south, lng: bounds.east },
});

// google-map-react rendered any child with lat/lng props as an absolutely
// positioned marker at that position. AdvancedMarker requires markers to be
// declared explicitly, so this wraps such children automatically to keep
// every caller's marker components unchanged.
const wrapPositionedChildren = (children) =>
  React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const { lat, lng, onClick } = child.props;
    if (lat === undefined || lng === undefined) return child;

    // AdvancedMarker does not propagate DOM click events from its custom HTML
    // content, so a child's onClick only fires if it's wired to the marker
    // itself (which also makes it gmpClickable).
    return (
      <AdvancedMarker
        key={child.key}
        position={{ lat: parseFloat(lat), lng: parseFloat(lng) }}
        anchorLeft="50%"
        anchorTop="100%"
        onClick={onClick}
      >
        {child}
      </AdvancedMarker>
    );
  });

const MARKERS_READY_TIMEOUT_MS = 8000;

// Waits for the map's first 'idle' event before creating any markers, since
// that's when Google's own internal setup is guaranteed to be ready --
// creating AdvancedMarkers any earlier is what triggers the race condition
// documented in https://github.com/visgl/react-google-maps/issues/877. Falls
// back to rendering anyway after a timeout so a map that's stuck (e.g. a
// persistent auth failure, where 'idle' may never fire) doesn't hide markers
// -- and therefore hide the resulting failure -- forever.
const MarkersReadyGate = ({ children }) => {
  const map = useMap();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!map || !window.google || isReady) return;

    const listener = window.google.maps.event.addListenerOnce(map, 'idle', () =>
      setIsReady(true)
    );
    const timeoutId = setTimeout(
      () => setIsReady(true),
      MARKERS_READY_TIMEOUT_MS
    );

    return () => {
      window.google.maps.event.removeListener(listener);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  if (!isReady) return null;
  return children;
};

// Runs inside the <Map> context so it can reach the google.maps.Map instance
// for the onGoogleApiLoaded callback and to re-center on center prop changes.
const MapController = ({ center, onGoogleApiLoaded }) => {
  const map = useMap();
  const hasNotifiedLoad = useRef(false);

  useEffect(() => {
    if (!map) return;
    if (hasNotifiedLoad.current) return;
    hasNotifiedLoad.current = true;
    if (onGoogleApiLoaded) {
      onGoogleApiLoaded({ map, maps: window.google && window.google.maps });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (map && center) map.panTo(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, center && center.lat, center && center.lng]);

  return null;
};

const BaseGoogleMapReact = ({
  center,
  children,
  onHoverOnStation,
  onHoverOffStation,
  onClickMarker = () => {},
  chargingStations = [],
  selectedStation,
  zoom,
  onChange,
  onGoogleApiLoaded,
  ...rest
}) => {
  const intl = useIntl();
  const mapLanguage = useRef(resolveGoogleMapLanguage(intl.locale)).current;

  if (!center) return null;

  const handleCameraChanged = (event) => {
    if (!onChange) return;
    const { center: newCenter, zoom: newZoom, bounds } = event.detail;
    onChange({
      center: newCenter,
      zoom: newZoom,
      bounds: boundsLiteralToCorners(bounds),
    });
  };

  return (
    <MapErrorBoundary>
      <APIProvider
        apiKey={API_KEY}
        language={mapLanguage}
        libraries={MAP_LIBRARIES}
      >
        <Map
          mapId={MAP_ID}
          defaultCenter={center}
          defaultZoom={zoom || INITIAL_ZOOM}
          onCameraChanged={handleCameraChanged}
          style={{ width: '100%', height: '100%' }}
          fullscreenControl={false}
          cameraControl={false}
          zoomControl={false}
          {...rest}
        >
          <CustomFullscreenControl />
          <CustomCameraControl />
          <MarkersReadyGate>
            {chargingStations.map((station) => (
              <AdvancedMarker
                key={station.id}
                position={{
                  lat: parseFloat(station.latitude),
                  lng: parseFloat(station.longitude),
                }}
                anchorLeft="50%"
                anchorTop="100%"
              >
                <ChargingStationsMapMarker
                  station={station}
                  selectedStation={selectedStation}
                  onClick={onClickMarker}
                  onMouseOver={() =>
                    onHoverOnStation && onHoverOnStation(station.id)
                  }
                  onMouseLeave={() => onHoverOffStation && onHoverOffStation()}
                />
              </AdvancedMarker>
            ))}
            {wrapPositionedChildren(children)}
          </MarkersReadyGate>
          <MapController
            center={center}
            onGoogleApiLoaded={onGoogleApiLoaded}
          />
        </Map>
      </APIProvider>
    </MapErrorBoundary>
  );
};

BaseGoogleMapReact.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  children: PropTypes.node,
  onClickMarker: PropTypes.func,
  onHoverOnStation: PropTypes.func,
  onHoverOffStation: PropTypes.func,
  chargingStations: PropTypes.array,
  selectedStation: PropTypes.object,
};

export default BaseGoogleMapReact;
